import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { receptionApi, type ReceptionRow } from "../../shared/api/reception";
import { workShiftsApi, workShiftsQueryKeys } from "../../shared/api/workShifts";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

export type DeskDoctor = { id: string; name: string; specialty: string | null };

/** Giờ hẹn theo giờ Việt Nam, dạng `HH:mm` — thứ API Lịch trực nhận. */
function vnTime(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: "Asia/Ho_Chi_Minh",
  }).format(new Date(iso));
}

/**
 * Bác sĩ đang trực ở đúng phòng của dòng này lên đầu (tiêu chí nghiệm thu #19088), phần còn lại
 * giữ nguyên thứ tự. Trả thêm tập đang trực để màn hình đánh dấu và cảnh báo.
 */
export function rankDoctors(
  doctors: DeskDoctor[],
  onDutyIds: string[] | undefined,
): { ordered: DeskDoctor[]; onDuty: Set<string> } {
  const onDuty = new Set(onDutyIds ?? []);
  return {
    ordered: [...doctors.filter((d) => onDuty.has(d.id)), ...doctors.filter((d) => !onDuty.has(d.id))],
    onDuty,
  };
}

/**
 * Chọn bác sĩ ngay trên dòng Tiếp đón (#19088) — thay cho sáu bước qua "Sửa lịch hẹn".
 *
 * Chỉ được vẽ khi server nói dòng này gán được (`doctorAssignable`) VÀ người dùng có
 * `queue.coordinate`; API vẫn tự kiểm lại cả hai. Chọn bác sĩ ngoài ca trực thì cảnh báo, không
 * chặn (quyết định 3).
 */
export function DeskDoctorPicker({
  row,
  doctors,
  date,
  open,
  onOpenChange,
  onAssigned,
  onError,
}: {
  row: ReceptionRow;
  doctors: DeskDoctor[];
  date: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAssigned: (offDuty: boolean) => void;
  onError: (m: string) => void;
}) {
  const [doctorId, setDoctorId] = useState("");
  const time = vnTime(row.startAt);
  const duty = useQuery({
    queryKey: workShiftsQueryKeys.duty(row.roomId ?? "", date, time),
    queryFn: () => workShiftsApi.duty(row.roomId!, date, time),
    enabled: Boolean(row.roomId),
    staleTime: 60_000,
  });
  const { ordered, onDuty } = rankDoctors(doctors, duty.data?.doctorIds);
  const rotaSpeaks = Boolean(duty.data?.shiftManaged);
  const offDutyPick = Boolean(doctorId) && rotaSpeaks && !onDuty.has(doctorId);

  const assign = useMutation({
    mutationFn: () => receptionApi.assignDoctor(row.appointmentId, doctorId),
    onSuccess: (res) => onAssigned(res.offDuty),
    onError: (e) => onError(apiError(e).detail),
  });

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <Select value={doctorId} onValueChange={setDoctorId} open={open} onOpenChange={onOpenChange}>
          <SelectTrigger className="w-[14rem] max-w-full" aria-label="Chọn bác sĩ">
            <SelectValue placeholder="Chọn bác sĩ" />
          </SelectTrigger>
          <SelectContent>
            {ordered.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.name}
                {onDuty.has(d.id) ? " · đang trực" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={!doctorId || assign.isPending}
          onClick={() => assign.mutate()}
        >
          Gán
        </Button>
      </div>
      {offDutyPick && (
        <span className="text-xs text-amber-700">
          Bác sĩ này không trong ca trực của phòng lúc {time} — vẫn gán được.
        </span>
      )}
    </div>
  );
}
